/**
 * nj-audit — who made a journey, and who changed it last.
 *
 * Before this, a journey document carried one field about its own history:
 * `updated`, a DISPLAY string ("Just now" / "—"). Nothing recorded an author, so
 * the journeys list could only report whether a row had been edited in the
 * designer at all — the comment at NewJourneys.tsx's row builder said exactly
 * that, and it was true.
 *
 * Two stamps, one shape:
 *
 *  • DESIGNER stamps (`from: "designer"`). Every write goes through NewJourneys'
 *    `saveDoc`, so that is the only place a stamp is minted: first write mints
 *    `createdAt` + `createdBy`, every write sets `updatedAt` + `updatedBy`. The
 *    actor is the REAL user, never the person an admin is acting as — same
 *    reasoning (and the same `getRealUser()` read) as `lib/audit.ts`, where
 *    reading `getUser()` recorded an admin's changes against the staff member
 *    they were impersonating.
 *
 *  • OWNER stamps (`from: "owner"`). A queue, event or appointment-catalogue row
 *    the designer has never opened has no document, so it has no designer stamp
 *    and inventing one would be a fabrication. Instead `ownerAudit()` reads the
 *    OWNING record's own created/modified fields. Those fields are not on the
 *    queue / event / catalogue ops yet — the ask is filed as
 *    `CD-API-REQUEST-journey-audit-2026-08-27.md` — so the reader is tolerant by
 *    design (camelCase or snake_case, actor as string or object) and returns
 *    `undefined` when the record says nothing. A row with nothing to say renders
 *    "Not edited yet", which is the honest answer until the fields ship.
 *
 * Timestamps are ISO 8601 on the wire and formatted through `lib/datetime.ts` at
 * render — never hand-built, per the standing date/time decision.
 */
import { getRealUser, getImpersonation } from "../../shell/access";
import { friendlyDateShort, friendlyDateTime, toDate } from "../../lib/datetime";

export interface JourneyActor {
  id?: string;
  name: string;
  email?: string;
  init?: string;
  /** Set when an admin was ACTING AS someone else; `name` is still the admin. */
  onBehalfOf?: { name: string; email?: string };
}

export interface JourneyAudit {
  createdAt?: string;
  createdBy?: JourneyActor;
  updatedAt?: string;
  updatedBy?: JourneyActor;
  /** Where these stamps came from — this designer, or the owning record. */
  from?: "designer" | "owner";
}

const initialsOf = (name: string): string =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";

/** The signed-in user, as a journey actor. Empty until a session is minted. */
export function currentActor(): JourneyActor | undefined {
  const u = getRealUser();
  const name = u.name || u.email || "";
  if (!name) return undefined;
  const actor: JourneyActor = { name, init: u.init || initialsOf(name) };
  if (u.id) actor.id = u.id;
  if (u.email) actor.email = u.email;
  const imp = getImpersonation();
  if (imp) {
    const target = imp.target.name || imp.target.email || "—";
    actor.onBehalfOf = imp.target.email ? { name: target, email: imp.target.email } : { name: target };
  }
  return actor;
}

/**
 * The audit block for a save. `prev` is the document's existing block: its
 * `createdAt`/`createdBy` are preserved if present, minted if not — so the first
 * save of a template-created, duplicated or freshly-derived journey is its
 * creation, and every save after it is a modification.
 */
export function stampSave(prev?: JourneyAudit, at: Date = new Date()): JourneyAudit {
  const actor = currentActor();
  const iso = at.toISOString();
  const created = prev?.from === "designer" ? prev.createdAt : undefined;
  const next: JourneyAudit = { from: "designer", createdAt: created ?? iso, updatedAt: iso };
  const createdBy = prev?.from === "designer" ? prev.createdBy : undefined;
  const keptCreator = created ? createdBy : actor;
  if (keptCreator) next.createdBy = keptCreator;
  if (actor) next.updatedBy = actor;
  return next;
}

/* ── Owner-record fallback ─────────────────────────────────────────────── */

const CREATED_AT_KEYS = ["createdAt", "created_at", "createdOn", "created_on", "created"];
const CREATED_BY_KEYS = ["createdBy", "created_by", "createdByName", "created_by_name", "creator"];
const UPDATED_AT_KEYS = ["updatedAt", "updated_at", "modifiedAt", "modified_at", "lastModifiedAt", "last_modified_at", "lastModified", "last_modified"];
const UPDATED_BY_KEYS = ["updatedBy", "updated_by", "modifiedBy", "modified_by", "lastModifiedBy", "last_modified_by"];

/** An ISO timestamp, or undefined when the value is absent, unparseable, or a
 *  display string like "Just now" that carries no instant. */
function pickIso(rec: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = rec[k];
    if (v == null || v === "") continue;
    if (typeof v !== "string" && typeof v !== "number" && !(v instanceof Date)) continue;
    const d = toDate(v as string | number | Date);
    if (d) return d.toISOString();
  }
  return undefined;
}

/** An actor from a string name or an object with name/email/id. */
function pickActor(rec: Record<string, unknown>, keys: string[]): JourneyActor | undefined {
  for (const k of keys) {
    const v = rec[k];
    if (!v) continue;
    if (typeof v === "string") {
      const name = v.trim();
      if (!name) continue;
      return { name, init: initialsOf(name) };
    }
    if (typeof v === "object") {
      const o = v as Record<string, unknown>;
      const name = String(o.name ?? o.displayName ?? o.fullName ?? o.email ?? "").trim();
      if (!name) continue;
      const actor: JourneyActor = { name, init: initialsOf(name) };
      if (o.id) actor.id = String(o.id);
      if (o.email) actor.email = String(o.email);
      return actor;
    }
  }
  return undefined;
}

/**
 * Read a queue / event / catalogue / board record's OWN created + modified
 * fields. Returns undefined when the record carries none — the caller then shows
 * "Not edited yet" rather than a made-up author.
 */
export function ownerAudit(rec: unknown): JourneyAudit | undefined {
  if (!rec || typeof rec !== "object") return undefined;
  const r = rec as Record<string, unknown>;
  const createdAt = pickIso(r, CREATED_AT_KEYS);
  const createdBy = pickActor(r, CREATED_BY_KEYS);
  const updatedAt = pickIso(r, UPDATED_AT_KEYS);
  const updatedBy = pickActor(r, UPDATED_BY_KEYS);
  if (!createdAt && !createdBy && !updatedAt && !updatedBy) return undefined;
  const out: JourneyAudit = { from: "owner" };
  if (createdAt) out.createdAt = createdAt;
  if (createdBy) out.createdBy = createdBy;
  if (updatedAt) out.updatedAt = updatedAt;
  if (updatedBy) out.updatedBy = updatedBy;
  return out;
}

/* ── Rendering ─────────────────────────────────────────────────────────── */

export const actorName = (a?: JourneyActor): string => (a?.name ? a.name : "");

/** Actor label including the acting-as note, e.g. "Nick (for Dana Reid)". */
export function actorLabel(a?: JourneyActor): string {
  if (!a?.name) return "";
  return a.onBehalfOf ? `${a.name} (for ${a.onBehalfOf.name})` : a.name;
}

export interface AuditCell {
  /** Who — "" when unknown. */
  who: string;
  /** When, short form ("Aug 27, 2026") — "" when unknown. */
  when: string;
  /** Full date + time, for the cell's title attribute. */
  title: string;
}

const cell = (actor: JourneyActor | undefined, iso: string | undefined, verb: string): AuditCell => {
  const who = actorLabel(actor);
  const when = iso ? friendlyDateShort(iso) : "";
  const parts: string[] = [];
  if (who) parts.push(`${verb} by ${who}`);
  if (iso) parts.push(friendlyDateTime(iso));
  return { who, when, title: parts.join(" · ") };
};

export const createdCell = (a?: JourneyAudit): AuditCell => cell(a?.createdBy, a?.createdAt, "Created");
export const modifiedCell = (a?: JourneyAudit): AuditCell => cell(a?.updatedBy, a?.updatedAt, "Last modified");

/** True when a block carries anything worth rendering. */
export const hasAudit = (a?: JourneyAudit): boolean =>
  !!(a && (a.createdAt || a.createdBy || a.updatedAt || a.updatedBy));
